// Epoch drill-down panel: the /income decomposition for one epoch, with
// lamports and SOL columns. Collapsed by default; [x] or Esc closes; a new
// epoch selection replaces the panel content.
//
// Sources: income channels from /income; the BAM SOL valuation and the net
// total come from /apy for the same epoch (bam_sol_lamports,
// net_total_lamports — the ledger's own valued figures) rather than being
// recomputed client-side. Null renders as an em dash, never 0.
//
// Focus: show() remembers whichever element had focus beforehand (read
// through the container's own root, since document.activeElement resolves
// to the shadow host — not the focused descendant — from outside an open
// shadow root) and restores it on hide(), so Esc (or [x]) always returns
// focus to the button that opened the panel.

import { closeIcon } from "./icons";
import type { ApyRow, IncomeRow } from "./api";
import { formatCount, formatLamports, formatUtcDateTime, lamportsToSol } from "./format";

interface TableRow {
  label: string;
  lamports: number | null;
  net?: boolean;
  /** Policy-state fact derived from measured data (see header comment); undefined renders no note. */
  note?: string;
}

function buildRows(income: IncomeRow | undefined, apy: ApyRow | undefined): TableRow[] {
  const mevCommissionZero = income?.mev_commission_bps === 0;
  const inflationCommissionZero = income?.inflation_commission_lamports === 0;
  return [
    { label: "Block fees", lamports: income?.block_fees_lamports ?? null },
    { label: "Tips gross (block-observed)", lamports: income?.tips_gross_lamports ?? null },
    { label: "Tips gross (Kobe-settled)", lamports: income?.tips_gross_kobe_lamports ?? null },
    {
      label: "MEV claimed",
      lamports: income?.mev_claimed_lamports ?? null,
      note: mevCommissionZero ? "0% MEV commission · all tips to stakers" : undefined,
    },
    {
      label: "Inflation commission",
      lamports: income?.inflation_commission_lamports ?? null,
      note: inflationCommissionZero ? "0% commission · all rewards to delegators" : undefined,
    },
    { label: "BAM boost (SOL value)", lamports: apy?.bam_sol_lamports ?? null },
    { label: "Vote cost (expense)", lamports: income?.vote_cost_lamports ?? null },
    { label: "Operator tx cost (expense)", lamports: income?.operator_tx_cost_lamports ?? null },
    { label: "Net", lamports: apy?.net_total_lamports ?? null, net: true },
  ];
}

export class DrilldownPanel {
  readonly #container: HTMLElement;
  readonly #onVisibility: ((open: boolean) => void) | undefined;
  #returnFocusTo: HTMLElement | null = null;
  #openEpoch: number | null = null;

  constructor(container: HTMLElement, onVisibility?: (open: boolean) => void) {
    this.#container = container;
    this.#onVisibility = onVisibility;
  }

  /** Epoch the panel currently shows, or null while hidden — lets the opener toggle instead of blindly re-opening. */
  get openEpoch(): number | null {
    return this.#openEpoch;
  }

  hide(): void {
    this.#openEpoch = null;
    this.#container.replaceChildren();
    this.#container.classList.add("vf-hidden");
    this.#onVisibility?.(false);
  }

  #hideAndReturnFocus(): void {
    this.hide();
    this.#returnFocusTo?.focus({ preventScroll: true });
  }

  show(epoch: number, income: IncomeRow | undefined, apy: ApyRow | undefined): void {
    const root = this.#container.getRootNode();
    this.#returnFocusTo =
      root instanceof ShadowRoot ? (root.activeElement as HTMLElement | null) : document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const panel = document.createElement("div");
    panel.className = "vf-drilldown";
    panel.tabIndex = -1;
    // Persistent filtered listener — never `{ once: true }`, which removes
    // the listener after the first keydown of ANY key (Tab, then Escape,
    // left the panel unclosable). The panel node is recreated per show(),
    // so the listener's lifetime is exactly the panel's.
    panel.addEventListener("keydown", (ev) => {
      if (ev.key === "Escape") {
        this.#hideAndReturnFocus();
      }
    });

    const head = document.createElement("div");
    head.className = "vf-drilldown-head";
    const title = document.createElement("span");
    title.className = "vf-drilldown-title";
    title.textContent = `Epoch ${epoch} income decomposition`;
    const close = document.createElement("button");
    close.className = "vf-btn";
    close.type = "button";
    close.classList.add("vf-close");
    close.append(closeIcon());
    close.setAttribute("aria-label", "close income drill-down");
    close.addEventListener("click", () => this.#hideAndReturnFocus());
    head.append(title, close);
    panel.append(head);

    if (income === undefined && apy === undefined) {
      const msg = document.createElement("div");
      msg.className = "vf-meta";
      msg.textContent = "No ledger row for this epoch (gap).";
      panel.append(msg);
    } else {
      const table = document.createElement("table");
      const thead = document.createElement("thead");
      const hr = document.createElement("tr");
      for (const h of ["Channel", "Lamports", "SOL"]) {
        const th = document.createElement("th");
        th.textContent = h;
        hr.append(th);
      }
      thead.append(hr);
      table.append(thead);
      const tbody = document.createElement("tbody");
      for (const row of buildRows(income, apy)) {
        const tr = document.createElement("tr");
        if (row.net === true) {
          tr.className = "vf-net";
        }
        const cells = [row.label, formatLamports(row.lamports), lamportsToSol(row.lamports)];
        for (const c of cells) {
          const td = document.createElement("td");
          td.textContent = c;
          tr.append(td);
        }
        // Note rides inside the SOL cell so the grid stays three columns;
        // a fourth <td> would open a table-wide gutter on every other row.
        // Prepended (and floated left in CSS) so the SOL figure keeps its
        // flush-right place-value alignment in the right-aligned column.
        if (row.note !== undefined) {
          const noteSpan = document.createElement("span");
          noteSpan.className = "vf-drilldown-note";
          noteSpan.textContent = row.note;
          tr.lastElementChild?.prepend(noteSpan);
        }
        tbody.append(tr);
      }
      table.append(tbody);
      panel.append(table);

      const meta = document.createElement("div");
      meta.className = "vf-meta";
      const mevBps = income?.mev_commission_bps ?? null;
      const bamJitosol = income?.bam_boost_entitled_jitosol ?? null;
      meta.textContent = [
        `Net = block fees + BAM + MEV claimed - vote cost - operator tx cost (ledger net_total).`,
        `Tips are transfers to stakers-side tip accounts, not fees; MEV commission that epoch: ` +
          `${mevBps === null ? "—" : `${formatCount(mevBps)} bps`}.`,
        `BAM entitled: ${bamJitosol === null ? "—" : `${lamportsToSol(bamJitosol)} JitoSOL`}.`,
        `Epoch window: ${formatUtcDateTime(income?.start_time ?? null)} -> ` +
          `${formatUtcDateTime(income?.end_time ?? null)}.`,
      ].join(" ");
      panel.append(meta);
    }

    this.#openEpoch = epoch;
    this.#container.replaceChildren(panel);
    this.#container.classList.remove("vf-hidden");
    this.#onVisibility?.(true);
    // No preventScroll here: an opener (pin-card details, an income-view
    // plot click) can sit a viewport away from this panel on small
    // embeds, and suppressing the scroll teleports focus to an element
    // that can be entirely off-screen (WCAG 2.4.11). The focus-RETURN
    // paths keep preventScroll — same-viewport by construction.
    panel.focus();
  }
}
